import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

// Lazy initializer for favorites state
const getInitialFavorites = () => {
  if (typeof window === 'undefined') return [];
  const savedFavorites = localStorage.getItem('festaEncantada_favorites');
  if (savedFavorites) {
    try {
      return JSON.parse(savedFavorites);
    } catch (e) {
      console.error('Error loading favorites:', e);
      localStorage.removeItem('festaEncantada_favorites');
    }
  }
  return [];
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(getInitialFavorites);

  // Save favorites to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('festaEncantada_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const addToFavorites = useCallback((product) => {
    setFavorites(prev => {
      if (prev.find(item => item.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });
  }, []);

  const removeFromFavorites = useCallback((productId) => {
    setFavorites(prev => prev.filter(item => item.id !== productId));
  }, []);

  const toggleFavorite = useCallback((product) => {
    setFavorites(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) {
        return prev.filter(item => item.id !== product.id);
      }
      return [...prev, product];
    });
  }, []);

  const isFavorite = useCallback((productId) => {
    return favorites.some(item => item.id === productId);
  }, [favorites]);

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  const getFavoritesCount = useCallback(() => {
    return favorites.length;
  }, [favorites]);

  const value = {
    favorites,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    getFavoritesCount
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
